import documentHistoryGuard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Helpers/documentHistoryGuard';
import DocumentViewerContext from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/DocumentViewerContext';
import { setActiveSection } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/activeSectionSignal';
import {
	closeDocumentPanel,
	openDocumentPanel,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/documentPanelSignal';
import { setEditingField } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/editingFieldSignal';
import pendingDiscardSignal, {
	setPendingDiscard,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/pendingDiscardSignal';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '@verclam/ui-kit';

/**
 * The single resolution point of the anti-loss guard: whatever gesture got blocked by a modified
 * draft (opening another field or panel, closing the sidebar, stepping to the next field, changing
 * section, leaving the screen by a link or by the browser's back button) is replayed here, once the
 * draft is actually discarded — `pendingDiscardSignal` only ever carries the primitives that resolution
 * needs. Reads the viewer context without requiring it: only the `step` and `list-action` intentions
 * need it, and they only ever queue under the provider. A replayed list gesture goes back through the
 * list-action handler, so it still opens its removal or verified-document confirmation.
 */
const DiscardChangesDialog: React.FC = () => {
	const pending = useSignalValue(pendingDiscardSignal);
	const navigate = useNavigate();
	const viewer = React.useContext(DocumentViewerContext);

	const handleAccept = React.useCallback(() => {
		if (pending === null) {
			return;
		}

		setEditingField(null);
		setPendingDiscard(null);

		switch (pending.type) {
			case 'tab':
				setActiveSection(pending.sectionKey);
				break;
			case 'field':
				setEditingField({ fieldKey: pending.fieldKey, isDirty: false });
				break;
			case 'panel':
				openDocumentPanel(pending.target);
				break;
			case 'close':
				closeDocumentPanel();
				break;
			case 'step':
				viewer?.goToAdjacentPending(pending.direction);
				break;
			case 'list-action':
				viewer?.listActionHandlerRef.current?.(pending.action);
				break;
			case 'back':
				// `replace` takes the place of the history sentinel the guard may have pushed.
				navigate(pending.path, { replace: true });
				break;
			case 'history-back':
				documentHistoryGuard.leaveByHistory();
				break;
		}
	}, [pending, navigate, viewer]);

	const handleReject = React.useCallback(() => {
		setPendingDiscard(null);
	}, []);

	return (
		<ConfirmDialog
			visible={pending !== null}
			onHide={handleReject}
			header="Abandonner les modifications ?"
			message="La valeur que vous avez modifiée n'est pas enregistrée."
			acceptLabel="Abandonner les modifications"
			rejectLabel="Continuer la modification"
			accept={handleAccept}
			reject={handleReject}
		/>
	);
};

export default DiscardChangesDialog;
